// Flash Flood Threat advisory PDF — same template as the Fire Risk forecast
// advisory (orange header, summary card, map, four count cards, key table,
// blue footer). It follows the map level tab that is selected (Suco, Post
// Admin or Municipality): the map colours that level, the cards count that
// level's units, and the key table lists that level's rows for the threat
// categories picked in the report dialog.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment-timezone";
import { preloadReportLogos, COLORS, HEADER_H } from "../../../common/reportTemplate/reportTemplate.js";
import {
    drawFireHeader, drawFireSummaryCard, drawRiskCountCards, drawTwoToneHeading, drawFireFooter,
    formatIssuedAt, FR_CONTENT_BOTTOM,
} from "../../FireRisk/Forecast/pdfLayout_FR.js";

// Threat categories, strongest first. Colours match the on-screen map.
export const THREAT_LEVELS = {
    high: { label: "Highly Likely", heading: "HIGHLY LIKELY", rgb: [230, 45, 58], hex: "#E62D3A", cardBg: COLORS.cardPink },
    likely: { label: "Likely", heading: "LIKELY", rgb: [243, 155, 58], hex: "#F39B3A", cardBg: COLORS.cardOrange },
    less: { label: "Less Likely", heading: "LESS LIKELY", rgb: [243, 235, 18], hex: "#F3EB12", cardBg: COLORS.cardTan },
    none: { label: "No Threat", heading: "NO THREAT", rgb: [222, 222, 223], hex: "#DEDEDF", cardBg: "#EDEDED" },
};
export const THREAT_ORDER = ["high", "likely", "less", "none"];

// Same thresholds as the map legend: 0/N/A none, up to 20 less likely,
// up to 40 likely, above 40 highly likely.
export const classifyThreat = (value) => {
    const v = parseFloat(value);
    if (!Number.isFinite(v) || v <= 0) return "none";
    if (v <= 20) return "less";
    if (v <= 40) return "likely";
    return "high";
};

const LEVELS = {
    suco: { levelText: "Suco Level", unit: "Suco", plural: "SUCOS" },
    post_admin: { levelText: "Post Admin Level", unit: "Post Admin", plural: "POST ADMINS" },
    municipality: { levelText: "Municipality Level", unit: "Municipality", plural: "MUNICIPALITIES" },
};

const TEXT = {
    en: {
        footer: "This advisory is valid for the next 24 hours.",
        page: "Page [i] of [pageCount]",
    },
    tet: {
        footer: "Avisu ne'e válidu ba oras 24 tuir mai.",
        page: "Pajina [i] husi [pageCount]",
    },
};

// ── Map: threat-coloured boundaries drawn straight from the GeoJSON ─────────
const MAP_W = 1200;
// Frame runs to -9.75 so the south coast isn't cropped.
const MAP_H = 525;
const BBOX = { minLng: 123.5, maxLng: 127.5, minLat: -9.75, maxLat: -8.0 };

function buildMapImage(geojson, rowByCode, adm1Data, level) {
    const canvas = document.createElement("canvas");
    canvas.width = MAP_W;
    canvas.height = MAP_H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#dbeaf5";
    ctx.fillRect(0, 0, MAP_W, MAP_H);

    const px = (lng, lat) => [
        ((lng - BBOX.minLng) / (BBOX.maxLng - BBOX.minLng)) * MAP_W,
        ((BBOX.maxLat - lat) / (BBOX.maxLat - BBOX.minLat)) * MAP_H,
    ];
    const tracePolygon = (rings) => {
        rings.forEach((ring) => {
            ring.forEach(([lng, lat], i) => {
                const [x, y] = px(lng, lat);
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            });
            ctx.closePath();
        });
    };
    const paint = (geom, { fillStyle, strokeStyle, lineWidth }) => {
        if (!geom) return;
        ctx.beginPath();
        if (geom.type === "Polygon") tracePolygon(geom.coordinates);
        else if (geom.type === "MultiPolygon") geom.coordinates.forEach(tracePolygon);
        if (fillStyle) { ctx.fillStyle = fillStyle; ctx.fill("evenodd"); }
        if (strokeStyle) { ctx.strokeStyle = strokeStyle; ctx.lineWidth = lineWidth; ctx.stroke(); }
    };

    (geojson?.features || []).forEach((f) => {
        const row = rowByCode.get(String(f.properties?.ADM_Code));
        const key = classifyThreat(row ? (row.max_value ?? row.value) : null);
        paint(f.geometry, {
            fillStyle: `rgb(${THREAT_LEVELS[key].rgb.join(",")})`,
            strokeStyle: "rgba(0, 0, 0, 0.6)",
            lineWidth: level === "suco" ? 0.6 : 1,
        });
    });

    // Municipality outlines on top when a finer level is shown
    if (level !== "municipality") {
        (adm1Data?.features || []).forEach((f) => {
            paint(f.geometry, { strokeStyle: "rgba(0, 0, 0, 0.9)", lineWidth: 1.8 });
        });
    }
    return canvas.toDataURL("image/png", 0.92);
}

// ── Public entry point ──────────────────────────────────────────────────────
/**
 * rows: the threat rows for `level` (adm_code, name, adm1, adm2, basin_id,
 * max_value); geojson: boundaries for that level; threatLevels: category keys
 * to list in the key table (e.g. ["high", "likely"]).
 */
export async function generateFlashFloodAdvisory({
    language = "en", level = "suco", rows = [], geojson, adm1Data, threatLevels = ["high", "likely"],
}) {
    const t = TEXT[language] || TEXT.en;
    const meta = LEVELS[level] || LEVELS.suco;

    const pdf = new jsPDF("portrait", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const headerHeight = HEADER_H + 5;
    const limitY = FR_CONTENT_BOTTOM;
    const logos = await preloadReportLogos();
    const headerOpts = { title: "TIMOR LESTE FLASH FLOOD THREAT ADVISORY", issuedAt: formatIssuedAt() };
    const drawHeader = () => drawFireHeader(pdf, logos, headerOpts);
    const addPageWithHeader = () => { pdf.addPage(); drawHeader(); return headerHeight + 5; };

    const classified = rows.map((r) => ({ ...r, threat: classifyThreat(r.max_value ?? r.value) }));
    const counts = Object.fromEntries(THREAT_ORDER.map((k) => [k, 0]));
    classified.forEach((r) => { counts[r.threat] += 1; });

    // ── Page 1: header, summary card ────────────────────────────────────────
    drawHeader();
    let currentY = headerHeight + 3;
    const forecastDate = moment.tz("Asia/Dili").format("DD MMMM YYYY");
    currentY = drawFireSummaryCard(pdf, currentY, {
        forecastDate,
        levelText: meta.levelText,
        heading: "FLASH FLOOD THREAT TODAY",
        description: "Forecast assesses the likelihood of flash flooding across Timor-Leste",
        icon: "drop",
    });
    currentY += 4;

    // ── Map ─────────────────────────────────────────────────────────────────
    try {
        const rowByCode = new Map(classified.map((r) => [String(r.adm_code), r]));
        const mapUrl = buildMapImage(geojson, rowByCode, adm1Data, level);
        const mapW = 158;
        const mapH = (MAP_H / MAP_W) * mapW;
        pdf.addImage(mapUrl, "PNG", (pageWidth - mapW) / 2, currentY, mapW, mapH);
        currentY += mapH + 7;
    } catch (e) {
        console.warn("[FlashFlood PDF] map failed:", e);
        pdf.setFont("helvetica", "italic").setFontSize(9).setTextColor(150, 150, 150);
        pdf.text("[Map image could not be generated]", 10, currentY + 5);
        pdf.setTextColor(0, 0, 0);
        currentY += 12;
    }

    // ── Threat count cards ──────────────────────────────────────────────────
    if (currentY + 62 > limitY) currentY = addPageWithHeader();
    currentY += 3;
    drawTwoToneHeading(pdf, currentY, "FLASH FLOOD THREAT LEVELS : ", `${meta.unit} count by forecast category`);
    currentY += 6;
    currentY = drawRiskCountCards(pdf, currentY, THREAT_ORDER.map((k) => ({
        bg: THREAT_LEVELS[k].cardBg,
        circle: THREAT_LEVELS[k].hex,
        heading: THREAT_LEVELS[k].heading,
        count: counts[k],
    })));
    currentY += 9;

    // ── Key table: only the threat categories picked in the dialog ──────────
    const order = Object.fromEntries(THREAT_ORDER.map((k, i) => [k, i]));
    const num = (r) => { const v = parseFloat(r.max_value ?? r.value); return Number.isFinite(v) ? v : -1; };
    const picked = classified
        .filter((r) => threatLevels.includes(r.threat))
        .sort((a, b) =>
            order[a.threat] - order[b.threat] ||
            num(b) - num(a) ||
            String(a.adm1 ?? a.name ?? "").localeCompare(String(b.adm1 ?? b.name ?? "")) ||
            String(a.adm2 ?? a.name ?? "").localeCompare(String(b.adm2 ?? b.name ?? "")) ||
            String(a.name ?? "").localeCompare(String(b.name ?? ""))
        );

    const placeCells = (r) => {
        if (level === "suco") return [r.adm1 ?? "N/A", r.adm2 ?? "N/A", r.name ?? "N/A"];
        if (level === "post_admin") return [r.adm1 ?? "N/A", r.name ?? "N/A"];
        return [r.name ?? "N/A"];
    };
    const body = picked.map((r, i) => {
        const v = r.max_value ?? r.value;
        return [
            i + 1,
            ...placeCells(r),
            r.basin_id != null && r.basin_id !== "" ? String(r.basin_id) : "N/A",
            Number.isFinite(parseFloat(v)) ? Number(v).toFixed(2) : "--",
            { content: THREAT_LEVELS[r.threat].label, styles: { fillColor: THREAT_LEVELS[r.threat].rgb } },
        ];
    });

    const placeHeads = level === "suco" ? ["Municipality", "Post Admin", "Suco"]
        : level === "post_admin" ? ["Municipality", "Post Admin"] : ["Municipality"];
    const widths = level === "suco" ? [10, 27, 31, 35, 22, 31, 36]
        : level === "post_admin" ? [10, 40, 48, 28, 32, 34] : [10, 64, 34, 40, 44];

    const headCell = (content, span = {}) => ({
        content, ...span, styles: { halign: "center", valign: "middle", fontStyle: "bold" },
    });
    const columnStyles = {};
    const lastIdx = widths.length - 1;
    widths.forEach((w, i) => {
        columnStyles[i] = {
            cellWidth: w,
            valign: "middle",
            halign: i === 0 || i === lastIdx || i === lastIdx - 2 ? "center" : i === lastIdx - 1 ? "right" : "left",
        };
    });

    if (currentY + 40 > limitY) currentY = addPageWithHeader();
    drawTwoToneHeading(pdf, currentY, "KEY TABLE : ", `HIGHEST THREAT ${meta.plural}`);
    currentY += 4;

    autoTable(pdf, {
        startY: currentY,
        head: [
            [
                headCell("Sl No", { rowSpan: 2 }),
                ...placeHeads.map((h) => headCell(h, { rowSpan: 2 })),
                headCell(moment.tz("Asia/Dili").format("YYYY-MM-DD"), { colSpan: 3 }),
            ],
            [headCell("Top Basin ID"), headCell("Forecast (mm)"), headCell("Threat")],
        ],
        body: body.length
            ? body
            : [[{ content: `No ${meta.unit.toLowerCase()} in the selected threat levels`, colSpan: widths.length, styles: { halign: "center" } }]],
        theme: "grid",
        rowPageBreak: "avoid", // never split a row across pages (leaves an empty sliver)
        headStyles: { fillColor: [225, 232, 242], textColor: [0, 0, 0], fontSize: 8.5, cellPadding: { top: 2.2, bottom: 2.2, left: 1.5, right: 1.5 } },
        styles: { fontSize: 8.5, cellPadding: { top: 2.2, bottom: 2.2, left: 1.8, right: 1.8 }, overflow: "linebreak", textColor: [85, 85, 85], lineWidth: 0.25, lineColor: [150, 150, 150], minCellHeight: 8 },
        columnStyles,
        // Keep rows clear of the footer bar; continuation pages get the header.
        margin: { left: 9, right: 9, top: headerHeight, bottom: pageHeight - FR_CONTENT_BOTTOM },
        didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(); },
    });

    // ── Footer + page label on every page ───────────────────────────────────
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        drawFireFooter(pdf, {
            message: t.footer,
            pageLabel: t.page.replace("[i]", String(i)).replace("[pageCount]", String(pageCount)),
        });
    }

    pdf.save("DRRDSS-FlashFloodThreat-Advisory.pdf");
}
